import {
  CButton,
  CButtonGroup,
  CCol,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CLink,
  CInputFile,
  CSelect,
  CTabs,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
} from "@coreui/react";
import { useEffect, useState, useRef } from "react";
import { Spinner } from "react-bootstrap";
import {
  createItem,
  updateItem,
  ItemRequestStatus,
  getItemPresignedUrl,
  getList,
  getItem,
} from "../../api/generics";
import Product, {
  newProduct,
  PRODUCTTYPES,
  StoreProductPricing,
} from "../../models/product";
import Errors, { getFieldErrors } from "../../models/errors";
import { SUCCESS } from "../../utils/constants/tags";
import { FieldErrors } from "../form/FieldErrors";
import { errorAlert, warningAlert } from "../utils/messages";
import { emptyValueOnUndefined } from "../../utils/fields";
import { RootState } from "../../store";
import { useSelector } from "react-redux";
import Color from "../../models/color";
import ColorsSelect from "../color/ColorsSelect";
import Size from "../../models/size";
import SizesSelect from "../size/SizesSelect";
import AnimatedCheckbox from "../checkbox/AnimatedCheckbox";
import CurrencyField from "../currencies/CurrencyField";
import { PYG } from "../../currency/available-currencies";
import CategorySelect from "../category/CategorySelect";
import Category from "../../models/category";
import ProductBrandSelect from "../product-brand/ProductBrandSelect";
import ProductBrand from "../../models/product-brand";
import {
  listToUniqueListItems,
  newUniqueListItem,
  UniqueListItem,
  uniqueListToListItems,
} from "../../models/unique-list-item";
import ProductCustomPricingFormItem from "./ProductCustomPricingFormItem";
import { VARIANT_EDIT } from "../../auth/permissions";

interface InputMarterialFormProps {
  initialProduct?: Product;
  initialErrors?: Errors;
  onCancel: () => void | Promise<void>;
  onSuccess: () => void | Promise<void>;
}

const SupplierForm: React.FC<InputMarterialFormProps> = ({
  initialProduct,
  initialErrors,
  onCancel,
  onSuccess,
}) => {
  const company = useSelector((state: RootState) => state.company.data.company);
  const [editingProduct, setEditingProduct] = useState<Product>(
    initialProduct ? initialProduct : newProduct()
  );
  const permissions = useSelector((state: RootState) => state.user.permissions);
  const [availableSizes, setAvailableSizes] = useState<Size[]>([]);
  const [colors, setColors] = useState<readonly Color[]>([]);
  const [sizes, setSizes] = useState<readonly Size[]>([]);
  const [loading, setLoading] = useState(true);
  const [isKidSize, setIsKidSize] = useState(false);
  const [showDetailedCost, setShowDetailedCost] = useState(false);
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [brand, setBrand] = useState<ProductBrand | undefined>(undefined);
  const [errors, setErrors] = useState<Errors>(
    initialErrors ? initialErrors : {}
  );
  const [submitting, setSubmitting] = useState(false);

  const [items, setItems] = useState<UniqueListItem<StoreProductPricing>[]>([]);

  const [loadedLogoPath, setLoadedLogoPath] = useState<string | undefined>(
    initialProduct?.imageUrl
  );
  const logo = useRef<HTMLInputElement>(null);
  const logoFileName = useRef<string>("");

  const logoModified = useRef<boolean>(false);

  // Al asignar una marca a un producto que no tenía, los códigos de barra ya
  // impresos se mantienen (el backend congela el prefijo usado originalmente).
  const brandBarcodeWarning =
    initialProduct?.id !== undefined &&
    initialProduct?.brandId === undefined &&
    brand !== undefined;

  const onIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct({
      ...editingProduct,
      name: e.target.value,
    });
  };

  const onDescriptionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct({
      ...editingProduct,
      description: e.target.value,
    });
  };

  const onProductTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct({
      ...editingProduct,
      productType: e.target.value,
    });
  };

  const onCodeIdentifierChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct({
      ...editingProduct,
      codeIdentifier: e.target.value,
    });
  };

  const onPriceChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      price: newAmount,
    });
  };
  // Writing the main cost by hand is an explicit override: the breakdown that
  // would otherwise compose it is cleared so the two can't disagree.
  const onProductionCostChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCost: newAmount,
      productionCostFabric: undefined,
      productionCostCleaning: undefined,
      productionCostCutting: undefined,
      productionCostAccesories: undefined,
      productionCostConfection: undefined,
      productionCostWashing: undefined,
      productionCostStoring: undefined,
      productionCostPocketLining: undefined,
    });
  };

  const onProductionCostFabricChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostFabric: newAmount,
    });
  };
  const onProductionCostCleaningChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostCleaning: newAmount,
    });
  };
  const onProductionCostCuttingChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostCutting: newAmount,
    });
  };
  const onProductionCostAccesoriesChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostAccesories: newAmount,
    });
  };
  const onProductionCostConfectionChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostConfection: newAmount,
    });
  };
  const onProductionCostWashingChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostWashing: newAmount,
    });
  };
  const onProductionCostStoringChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      productionCostStoring: newAmount,
    });
  };
  const onProductionCostPocketLiningChange = (
    newAmount: number | undefined
  ) => {
    setEditingProduct({
      ...editingProduct,
      productionCostPocketLining: newAmount,
    });
  };

  const onWholesalePriceChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      wholesalePrice: newAmount,
    });
  };

  const onSuperWholesalePriceChange = (newAmount: number | undefined) => {
    setEditingProduct({
      ...editingProduct,
      superWholesalePrice: newAmount,
    });
  };

  const onThresholdAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, wholesaleThreshold: amount };
    });
  };

  const onSuperThresholdAmountChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setEditingProduct((editingItem) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseInt(e.target.value))) {
        amount = parseInt(e.target.value);
      }

      return { ...editingItem, superWholesaleThreshold: amount };
    });
  };

  const onAverageFabricChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct((prevEditingProduct) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseFloat(e.target.value))) {
        amount = parseFloat(e.target.value);
      }

      return { ...prevEditingProduct, averageFabricUsage: amount };
    });
  };

  const onFabricWidthChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEditingProduct((prevEditingProduct) => {
      let amount: number | undefined = undefined;

      if (!isNaN(parseFloat(e.target.value))) {
        amount = parseFloat(e.target.value);
      }

      return { ...prevEditingProduct, fabricWidth: amount };
    });
  };

  const onLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    logoModified.current = true;
    let fileParts = e.target.value.split("\\");
    logoFileName.current = fileParts[fileParts.length - 1];
    setEditingProduct({
      ...editingProduct,
      imageUrl: logoFileName.current,
    });
  };

  const onKidsSizeChange = (isKidsSize: boolean) => {
    setIsKidSize(isKidSize);
    const newSizes: readonly Size[] = availableSizes
      .filter((size) => size.isKidSize === isKidsSize)
      .map((size) => ({ ...size }));
    setSizes(newSizes);
  };

  const onShowDetailedCostChange = (showDetails: boolean) => {
    setShowDetailedCost(showDetails);
  };

  // Wipes every sub-price and unlocks the main cost for direct entry.
  const onClearDetailedCost = () => {
    setEditingProduct({
      ...editingProduct,
      productionCostFabric: undefined,
      productionCostCleaning: undefined,
      productionCostCutting: undefined,
      productionCostAccesories: undefined,
      productionCostConfection: undefined,
      productionCostWashing: undefined,
      productionCostStoring: undefined,
      productionCostPocketLining: undefined,
    });
  };

  const onAddEntryClick = () => {
    setItems((items) => {
      const initialValue: StoreProductPricing = {};
      const newItems = [
        ...items,
        newUniqueListItem<StoreProductPricing>(initialValue),
      ];
      return newItems;
    });
  };

  const onEntryChange = (entry: UniqueListItem<StoreProductPricing>) => {
    setItems((items) => {
      const newItems = [...items];
      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems[index] = entry;

      return newItems;
    });
  };

  const onEntryDelete = (entry: UniqueListItem<StoreProductPricing>) => {
    setItems((items) => {
      const newItems = [...items];

      const index = newItems.findIndex((item) => {
        return item.uuid === entry.uuid;
      });

      if (index === -1) {
        return newItems;
      }

      newItems.splice(index, 1);

      return newItems;
    });
  };

  const handleLogoUpload = async () => {
    if (!logoModified.current) {
      return true;
    }

    const mutableColors: Color[] = Array.from(colors);
    const mutableSizes: Size[] = Array.from(sizes);

    const toSendItems: StoreProductPricing[] = uniqueListToListItems(items);

    toSendItems.forEach((item) => {
      item.companyId = company.id;

      item.productId = editingProduct.id;
      if (item.wholesaleThreshold === 0) {
        item.wholesaleThreshold = undefined;
      }
      if (item.superWholesaleThreshold === 0) {
        item.superWholesaleThreshold = undefined;
      }
      // Un precio en 0 no es un override valido: el POS lee estos campos con un
      // chequeo truthy y volveria igual al precio general, asi que se manda
      // directamente vacio.
      if (item.price === 0) {
        item.price = undefined;
      }
      if (item.wholesalePrice === 0) {
        item.wholesalePrice = undefined;
      }
      if (item.superWholesalePrice === 0) {
        item.superWholesalePrice = undefined;
      }
    });

    // Una fila vale la pena guardarla si define cualquier cosa propia de la
    // sucursal: un limite o un precio. Filtrar solo por limites descartaba en
    // silencio a las sucursales que unicamente cambian precios.
    const filteredItems = toSendItems.filter(
      (item) =>
        item.wholesaleThreshold !== undefined ||
        item.superWholesaleThreshold !== undefined ||
        item.price !== undefined ||
        item.wholesalePrice !== undefined ||
        item.superWholesalePrice !== undefined
    );

    setSubmitting(true);
    let toSendProduct = {
      ...editingProduct,
      companyId: company.id,
      colors: mutableColors,
      sizes: mutableSizes,
      categoryName: category ? category.name : undefined,
      categoryId: category ? category.id : undefined,
      brandName: brand ? brand.name : undefined,
      brandId: brand ? brand.id : undefined,
      items: filteredItems,
    };

    let presignedUrlPromise: Promise<ItemRequestStatus<{ url: string }>>;

    presignedUrlPromise = getItemPresignedUrl<Product>(
      `/clothing_products/put_logo_url/`,
      toSendProduct
    );

    const logoUrlStatus = await presignedUrlPromise;
    if (logoUrlStatus.status !== SUCCESS) {
      if (logoUrlStatus.errors !== undefined) {
        setErrors(logoUrlStatus.errors);
        Object.entries(logoUrlStatus.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach((message) => {
              // Si el campo es __root__, mostrar solo el mensaje sin el nombre del campo
              if (field === "__root__") {
                errorAlert(message as string);
              } else {
                errorAlert(`${field}: ${message}`);
              }
            });
          }
        });
      }

      let message = "Ha ocurrido un error!!";
      if (logoUrlStatus.detail !== undefined) {
        message = logoUrlStatus.detail;
      }
      errorAlert(message);
      return false;
    }

    if (logoUrlStatus.data === undefined) {
      return false;
    }

    const urlData = logoUrlStatus.data as { url: string };

    if (!logo.current || !logo.current.files) {
      return false;
    }
    const logoFile = logo.current.files[0];
    if (!logoFile) {
      return false;
    }

    const uploadStatus = await fetch(urlData.url!, {
      method: "PUT",
      body: logoFile,
      headers: {
        "Content-type": logoFile.type,
      },
    });

    return uploadStatus.status === 200;
  };

  const onSave = async () => {
    setSubmitting(true);
    const hasLogo = editingProduct.imageUrl !== undefined;

    const imageUploaded = hasLogo ? await handleLogoUpload() : true;
    if (!imageUploaded) {
      setSubmitting(false);
      return;
    }

    const mutableColors: Color[] = Array.from(colors);
    const mutableSizes: Color[] = Array.from(sizes);

    const toSendItems: StoreProductPricing[] = uniqueListToListItems(items);
    toSendItems.forEach((item) => {
      item.companyId = company.id;

      item.productId = editingProduct.id;
      if (item.wholesaleThreshold === 0) {
        item.wholesaleThreshold = undefined;
      }
      if (item.superWholesaleThreshold === 0) {
        item.superWholesaleThreshold = undefined;
      }
      // Un precio en 0 no es un override valido: el POS lee estos campos con un
      // chequeo truthy y volveria igual al precio general, asi que se manda
      // directamente vacio.
      if (item.price === 0) {
        item.price = undefined;
      }
      if (item.wholesalePrice === 0) {
        item.wholesalePrice = undefined;
      }
      if (item.superWholesalePrice === 0) {
        item.superWholesalePrice = undefined;
      }
    });

    // Una fila vale la pena guardarla si define cualquier cosa propia de la
    // sucursal: un limite o un precio. Filtrar solo por limites descartaba en
    // silencio a las sucursales que unicamente cambian precios.
    const filteredItems = toSendItems.filter(
      (item) =>
        item.wholesaleThreshold !== undefined ||
        item.superWholesaleThreshold !== undefined ||
        item.price !== undefined ||
        item.wholesalePrice !== undefined ||
        item.superWholesalePrice !== undefined
    );

    let toSendProduct = {
      ...editingProduct,
      companyId: company.id,
      colorsIds: colors,
      colors: mutableColors,
      sizes: mutableSizes,
      categoryName: category ? category.name : undefined,
      categoryId: category ? category.id : undefined,
      brandName: brand ? brand.name : undefined,
      brandId: brand ? brand.id : undefined,
      items: filteredItems,
    };

    if (
      toSendProduct.id === undefined &&
      toSendProduct.wholesaleThreshold === undefined
    ) {
      toSendProduct.wholesaleThreshold = 3;
    }

    if (toSendProduct.productType === "") {
      toSendProduct.productType = undefined;
    }

    let requestPromise: Promise<ItemRequestStatus<Product>>;
    if (editingProduct.id === undefined) {
      requestPromise = createItem<Product>(
        "/clothing_products/create/",
        toSendProduct
      );
    } else {
      requestPromise = updateItem<Product>(
        `/clothing_products/${toSendProduct.id}/`,
        toSendProduct
      );
    }

    const productStatus = await requestPromise;

    if (productStatus.status !== SUCCESS) {
      if (productStatus.errors !== undefined) {
        setErrors(productStatus.errors);
        Object.entries(productStatus.errors).forEach(([field, messages]) => {
          if (Array.isArray(messages)) {
            messages.forEach((message) => {
              // Si el campo es __root__, mostrar solo el mensaje sin el nombre del campo
              if (field === "__root__") {
                errorAlert(message as string);
              } else {
                errorAlert(`${field}: ${message}`);
              }
            });
          }
        });
      }
      let message = "Ha ocurrido un error!!";
      if (productStatus.detail !== undefined) {
        message = productStatus.detail;
      }
      errorAlert(message);
    } else {
      setErrors({});
      clearForm();
      setAvailableSizes([]);
      onSuccess();
    }
    setSubmitting(false);
  };

  const onClose = () => {
    clearForm();
    onCancel();
  };

  const clearForm = () => {
    setEditingProduct(newProduct());
    setColors([]);
    setSizes([]);
    setCategory(undefined);
    setLoading(false);
    setLoadedLogoPath(undefined);
    setIsKidSize(false);
    setShowDetailedCost(false);
    setItems([]);
  };

  const getColors = async () => {
    if (initialProduct === undefined || initialProduct.id === undefined) {
      setColors([]);
      return [];
    }
    const productIdStr = initialProduct.id.toString();
    if (productIdStr === undefined) {
      setColors([]);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("clothing_product_id", productIdStr);
    const colorsStatus = await getList<Color>(
      "/colors/",
      limit,
      offset,
      additional
    );
    if (colorsStatus.status === SUCCESS) {
      if (colorsStatus.data !== undefined) {
        setColors(colorsStatus.data.items);
      }
    } else {
      setColors([]);
    }
  };

  const getSizes = async () => {
    if (initialProduct === undefined || initialProduct.id === undefined) {
      setSizes([]);
      setIsKidSize(false);
      return [];
    }
    const productIdStr = initialProduct.id.toString();
    if (productIdStr === undefined) {
      setSizes([]);
      setIsKidSize(false);
      return [];
    }
    const limit = 100;
    const offset = 0;
    const additional = new Map();
    additional.set("clothing_product_id", productIdStr);
    const sizesStatus = await getList<Size>(
      "/sizes/",
      limit,
      offset,
      additional
    );
    if (sizesStatus.status === SUCCESS) {
      if (sizesStatus.data !== undefined) {
        setSizes(sizesStatus.data.items);
        setIsKidSize(
          sizesStatus.data.items.some((size) => size.isKidSize === true)
        );
      }
    } else {
      setSizes([]);
      setIsKidSize(false);
    }
  };

  const onBrandChange = (newBrand: ProductBrand | null) => {
    setEditingProduct((editingProduct) => {
      return {
        ...editingProduct,
        brandId: newBrand !== null ? newBrand.id : undefined,
        brandName: newBrand !== null ? newBrand.name : undefined,
      };
    });
    setBrand(newBrand !== null ? newBrand : undefined);
  };

  const fetchBrand = async (id: number) => {
    const brandStatus = await getItem<ProductBrand>(`/product_brands/${id}/`);
    if (brandStatus.status === SUCCESS) {
      if (brandStatus.data !== undefined) {
        setBrand(brandStatus.data);
      }
    } else {
      const message = brandStatus.detail
        ? brandStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
  };

  const onCategoryChange = (newCategory: Category | null) => {
    setEditingProduct((editingProduct) => {
      return {
        ...editingProduct,
        categoryId: newCategory !== null ? newCategory.id : undefined,
        categoryName: newCategory !== null ? newCategory.name : undefined,
      };
    });
    setCategory(newCategory !== null ? newCategory : undefined);
  };

  const fetchAvailableSizes = async () => {
    const limit = 100;
    const offset = 0;
    const sizesStatus = await getList<Size>(
      "/sizes/",
      limit,
      offset,
      undefined
    );
    if (sizesStatus.status === SUCCESS) {
      if (sizesStatus.data !== undefined) {
        setAvailableSizes(sizesStatus.data.items);
      }
    } else {
      setAvailableSizes([]);
    }
  };

  const fetchItems = async () => {
    if (initialProduct === undefined || initialProduct.id === undefined) {
      setItems([]);
      return [];
    }
    const producttIdStr = initialProduct.id.toString();
    if (producttIdStr === undefined) {
      setItems([]);
      return [];
    }
    let additionalParams: Map<string, string> | undefined = new Map<
      string,
      string
    >();
    additionalParams.set("product_id", producttIdStr);
    const limit = 100;
    const offset = 0;
    const itemsStatus = await getList<StoreProductPricing>(
      `/clothing_products/custom_thresholds/`,
      limit,
      offset,
      additionalParams
    );
    if (itemsStatus.status === SUCCESS) {
      if (itemsStatus.data !== undefined) {
        setItems(listToUniqueListItems(itemsStatus.data.items));
      }
    } else {
      setItems([]);
    }
  };

  const fetchCategory = async (id: number) => {
    const categoriesStatus = await getItem<Category>(`/categories/${id}/`);
    if (categoriesStatus.status === SUCCESS) {
      if (categoriesStatus.data !== undefined) {
        setCategory(categoriesStatus.data);
      }
    } else {
      const message = categoriesStatus.detail
        ? categoriesStatus.detail
        : "Error desconocido";
      warningAlert(message);
    }
  };

  useEffect(() => {
    setLoading(true);
    setEditingProduct(initialProduct ? initialProduct : newProduct());
    setLoadedLogoPath(initialProduct?.imageUrl);
    getColors();
    getSizes();
    if (initialProduct?.categoryId) {
      fetchCategory(initialProduct.categoryId);
    } else {
      setCategory(undefined);
    }
    if (initialProduct?.brandId) {
      fetchBrand(initialProduct.brandId);
    } else {
      setBrand(undefined);
    }
    fetchAvailableSizes();
    fetchItems();
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProduct]);

  useEffect(() => {
    setErrors(initialErrors ? initialErrors : {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialErrors]);

  // While the detailed cost breakdown is open and at least one sub-price has a
  // value, the main production cost is driven by the sum of the sub-prices.
  // If every sub-price is empty we leave the main cost untouched so it can be
  // written directly by hand.
  useEffect(() => {
    if (!showDetailedCost) {
      return;
    }
    const detailValues = [
      editingProduct.productionCostFabric,
      editingProduct.productionCostCleaning,
      editingProduct.productionCostCutting,
      editingProduct.productionCostAccesories,
      editingProduct.productionCostConfection,
      editingProduct.productionCostWashing,
      editingProduct.productionCostStoring,
      editingProduct.productionCostPocketLining,
    ];
    const hasValues = detailValues.some(
      (value) => value !== undefined && value !== null
    );
    if (!hasValues) {
      return;
    }
    const sum = detailValues.reduce<number>((total, value) => total + (value || 0), 0);
    setEditingProduct((prev) =>
      prev.productionCost === sum ? prev : { ...prev, productionCost: sum }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    showDetailedCost,
    editingProduct.productionCostFabric,
    editingProduct.productionCostCleaning,
    editingProduct.productionCostCutting,
    editingProduct.productionCostAccesories,
    editingProduct.productionCostConfection,
    editingProduct.productionCostWashing,
    editingProduct.productionCostStoring,
    editingProduct.productionCostPocketLining,
  ]);

  const buttonText1 = loading ? "Cargando..." : "Guardar";
  const buttonText = submitting ? "Guardando..." : buttonText1;

  const itemErrors = getFieldErrors("storeCustomPricing", errors) as Errors[];

  const detailedCostFields = [
    editingProduct.productionCostFabric,
    editingProduct.productionCostCleaning,
    editingProduct.productionCostCutting,
    editingProduct.productionCostAccesories,
    editingProduct.productionCostConfection,
    editingProduct.productionCostWashing,
    editingProduct.productionCostStoring,
    editingProduct.productionCostPocketLining,
  ];
  const hasDetailedCostValues = detailedCostFields.some(
    (value) => value !== undefined && value !== null
  );
  const detailedCostSum = detailedCostFields.reduce<number>(
    (total, value) => total + (value || 0),
    0
  );
  // When the breakdown is open and populated the main field mirrors the sum and
  // is locked; otherwise the user can type the cost directly.
  const isCostAutoCalculated = showDetailedCost && hasDetailedCostValues;
  const formatPyg = (amount: number) =>
    `₲${new Intl.NumberFormat("de-DE").format(amount)}`;

  return (
    <>
      <fieldset disabled={submitting}>
        <CTabs activeTab="general">
          <CNav variant="tabs">
            <CNavItem>
              <CNavLink data-tab="general">Datos de Producto</CNavLink>
            </CNavItem>

            <CNavItem>
              <CNavLink data-tab="custom">Límites por sucursal</CNavLink>
            </CNavItem>
          </CNav>
          <CTabContent className={"mt-2"}>
            <CTabPane data-tab="general">
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Código de prenda:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(
                        editingProduct.codeIdentifier
                      )}
                      onChange={onCodeIdentifierChange}
                      placeholder="Código de prenda"
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors("code_identifier", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Identificador:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingProduct.name)}
                      onChange={onIdentifierChange}
                      placeholder="Nombre de Producto"
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("name", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Descripción:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="text"
                      value={emptyValueOnUndefined(editingProduct.description)}
                      onChange={onDescriptionChange}
                      placeholder="Descripción"
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("description", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Categoría:</CLabel>
                  </CCol>

                  <CCol>
                    <CategorySelect
                      value={category ? category : null}
                      onChange={onCategoryChange}
                    ></CategorySelect>
                    <FieldErrors
                      errors={
                        getFieldErrors("categoryName", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Marca:</CLabel>
                  </CCol>

                  <CCol>
                    <ProductBrandSelect
                      value={brand ? brand : null}
                      onChange={onBrandChange}
                    ></ProductBrandSelect>
                    {brandBarcodeWarning ? (
                      <small className="text-warning">
                        Los códigos de barra ya generados no se modificarán al
                        asignar una marca por primera vez, para no invalidar los
                        stickers impresos.
                      </small>
                    ) : (
                      <></>
                    )}
                    <FieldErrors
                      errors={getFieldErrors("brandId", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel className={"mt-2"}>Tipo de producto:</CLabel>
                  </CCol>
                  <CCol>
                    <CSelect
                      type="text"
                      defaultValue={emptyValueOnUndefined(
                        editingProduct.productType
                      )}
                      value={emptyValueOnUndefined(editingProduct.productType)}
                      onChange={onProductTypeChange}
                    >
                      <option value={""}>-------</option>
                      {Object.entries(PRODUCTTYPES).map(([key, entry], ix) => {
                        return (
                          <option key={ix} value={key}>
                            {entry}
                          </option>
                        );
                      })}
                    </CSelect>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Precio de Costo:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CurrencyField
                      currency={PYG}
                      value={editingProduct.productionCost}
                      onChange={onProductionCostChange}
                      disabled={isCostAutoCalculated}
                    ></CurrencyField>
                    {isCostAutoCalculated ? (
                      <small className="text-muted">
                        <i className="fa fa-calculator mr-1" />
                        Calculado desde el desglose de costos
                      </small>
                    ) : (
                      <></>
                    )}
                    <FieldErrors
                      errors={
                        getFieldErrors("productionCost", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>Ver detalles de Costo:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CRow>
                      <AnimatedCheckbox
                        value={showDetailedCost}
                        onChange={onShowDetailedCostChange}
                      ></AnimatedCheckbox>
                    </CRow>
                  </CCol>
                </CRow>
              </CFormGroup>
              {showDetailedCost ? (
                <div
                  style={{
                    borderLeft: "4px solid #321fdb",
                    backgroundColor: "rgba(50, 31, 219, 0.04)",
                    borderRadius: "6px",
                    padding: "16px",
                    marginBottom: "1rem",
                  }}
                >
                  <div
                    className="d-flex align-items-center mb-3"
                    style={{ color: "#321fdb" }}
                  >
                    <i className="fa fa-sitemap mr-2" />
                    <strong>Desglose del Precio de Costo</strong>
                    <small className="text-muted ml-2">
                      La suma de estos importes compone el precio de costo.
                    </small>
                  </div>
                  <CFormGroup>
                    <CRow>
                      <CCol md={2}>
                        <CLabel>Precio promedio de tela:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostFabric}
                          onChange={onProductionCostFabricChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostFabric",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                      <CCol md={2}>
                        <CLabel>Precio Aseo:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostCleaning}
                          onChange={onProductionCostCleaningChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostCleaning",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                    </CRow>
                  </CFormGroup>
                  <CFormGroup>
                    <CRow>
                      <CCol md={2}>
                        <CLabel>Precio Corte:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostCutting}
                          onChange={onProductionCostCuttingChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostCutting",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                      <CCol md={2}>
                        <CLabel>Precio Accesorios:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostAccesories}
                          onChange={onProductionCostAccesoriesChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostAccesories",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                    </CRow>
                  </CFormGroup>
                  <CFormGroup>
                    <CRow>
                      <CCol md={2}>
                        <CLabel>Precio Confección:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostConfection}
                          onChange={onProductionCostConfectionChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostConfection",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                      <CCol md={2}>
                        <CLabel>Precio Lavado:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostWashing}
                          onChange={onProductionCostWashingChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostWashing",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                    </CRow>
                  </CFormGroup>
                  <CFormGroup>
                    <CRow>
                      <CCol md={2}>
                        <CLabel>Precio Empaquetado:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostStoring}
                          onChange={onProductionCostStoringChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostStoring",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                      <CCol md={2}>
                        <CLabel>Precio Forro Bolsillo:</CLabel>
                      </CCol>
                      <CCol md={4}>
                        <CurrencyField
                          currency={PYG}
                          value={editingProduct.productionCostPocketLining}
                          onChange={onProductionCostPocketLiningChange}
                        ></CurrencyField>
                        <FieldErrors
                          errors={
                            getFieldErrors(
                              "productionCostPocketLining",
                              errors
                            ) as string[]
                          }
                        ></FieldErrors>
                      </CCol>
                    </CRow>
                  </CFormGroup>
                  <hr style={{ borderColor: "rgba(50, 31, 219, 0.2)" }} />
                  <CRow>
                    <CCol className="d-flex justify-content-between align-items-center">
                      <CButton
                        color="link"
                        className="p-0 text-danger"
                        disabled={!hasDetailedCostValues}
                        onClick={onClearDetailedCost}
                      >
                        <i className="fa fa-eraser mr-1" />
                        Limpiar desglose
                      </CButton>
                      <span>
                        <span className="text-muted mr-2">
                          Total del desglose:
                        </span>
                        <strong
                          style={{ color: "#321fdb", fontSize: "1.1rem" }}
                        >
                          {formatPyg(detailedCostSum)}
                        </strong>
                      </span>
                    </CCol>
                  </CRow>
                </div>
              ) : (
                <></>
              )}
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Precio Mayorista:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CurrencyField
                      currency={PYG}
                      value={editingProduct.wholesalePrice}
                      onChange={onWholesalePriceChange}
                    ></CurrencyField>
                    <FieldErrors
                      errors={
                        getFieldErrors("wholesalePrice", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>Unidades para precio Mayorista:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CInput
                      type="number"
                      placeholder="Cantidad"
                      value={
                        editingProduct.wholesaleThreshold !== undefined
                          ? editingProduct.wholesaleThreshold
                          : 3
                      }
                      onChange={onThresholdAmountChange}
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors("wholesaleThreshold", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Precio Super Mayorista:</CLabel>
                  </CCol>
                  <CCol>
                    <CurrencyField
                      currency={PYG}
                      value={editingProduct.superWholesalePrice}
                      onChange={onSuperWholesalePriceChange}
                    ></CurrencyField>
                    <FieldErrors
                      errors={
                        getFieldErrors(
                          "superWholesalePrice",
                          errors
                        ) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>Unidades para precio Super Mayorista:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CInput
                      type="number"
                      placeholder="Cantidad"
                      value={emptyValueOnUndefined(
                        editingProduct.superWholesaleThreshold
                      )}
                      onChange={onSuperThresholdAmountChange}
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors(
                          "superWholesaleThreshold",
                          errors
                        ) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Precio Unitario:</CLabel>
                  </CCol>
                  <CCol md={4}>
                    <CurrencyField
                      currency={PYG}
                      value={editingProduct.price}
                      onChange={onPriceChange}
                    ></CurrencyField>
                    <FieldErrors
                      errors={getFieldErrors("price", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Promedio de Tela(cantidad):</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="number"
                      placeholder="Promedio."
                      value={
                        editingProduct.averageFabricUsage !== undefined
                          ? editingProduct.averageFabricUsage
                          : 0
                      }
                      onChange={onAverageFabricChange}
                      step="0.01"
                    ></CInput>
                    <FieldErrors
                      errors={
                        getFieldErrors("averageFabricUsage", errors) as string[]
                      }
                    ></FieldErrors>
                  </CCol>
                  <CCol md={2}>
                    <CLabel>Ancho de tela:</CLabel>
                  </CCol>
                  <CCol>
                    <CInput
                      type="number"
                      placeholder="Ancho de tela."
                      value={
                        editingProduct.fabricWidth !== undefined
                          ? editingProduct.fabricWidth
                          : 0
                      }
                      onChange={onFabricWidthChange}
                      step="0.01"
                    ></CInput>
                    <FieldErrors
                      errors={getFieldErrors("fabricWidth", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Imagen:</CLabel>
                  </CCol>
                  <CCol>
                    <CInputFile
                      accept=".png, .jpg"
                      type="file"
                      innerRef={logo}
                      onChange={onLogoChange}
                    ></CInputFile>
                    {loadedLogoPath ? (
                      <small>
                        Seleccionado actualmente:
                        <CLink href={`#`} target="_blank">
                          {loadedLogoPath}
                        </CLink>
                      </small>
                    ) : (
                      <></>
                    )}

                    <FieldErrors
                      errors={getFieldErrors("imageUrl", errors) as string[]}
                    ></FieldErrors>
                  </CCol>
                </CRow>
              </CFormGroup>
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Colores:</CLabel>
                  </CCol>
                  <CCol>
                    {initialProduct?.id === undefined ||
                    permissions?.includes(VARIANT_EDIT) ? (
                      <>
                        <ColorsSelect
                          value={colors}
                          onChange={setColors}
                          isDisabled={loading || submitting}
                        ></ColorsSelect>
                        <FieldErrors
                          errors={getFieldErrors("colors", errors) as string[]}
                        ></FieldErrors>
                      </>
                    ) : (
                      <p>{colors.map((color) => color.color).join(", ")}</p>
                    )}
                  </CCol>
                </CRow>
              </CFormGroup>
              {initialProduct?.id === undefined ||
              permissions?.includes(VARIANT_EDIT) ? (
                <CFormGroup>
                  <CRow>
                    <CCol md={2}>
                      <CLabel>Prenda para niños:</CLabel>
                    </CCol>
                    <CCol md={2}>
                      <CRow>
                        <AnimatedCheckbox
                          value={isKidSize}
                          onChange={onKidsSizeChange}
                        ></AnimatedCheckbox>
                      </CRow>
                    </CCol>
                  </CRow>
                </CFormGroup>
              ) : (
                <></>
              )}
              <CFormGroup>
                <CRow>
                  <CCol md={2}>
                    <CLabel>Talles:</CLabel>
                  </CCol>
                  <CCol>
                    {initialProduct?.id === undefined ||
                    permissions?.includes(VARIANT_EDIT) ? (
                      <>
                        <SizesSelect
                          value={sizes}
                          onChange={setSizes}
                          isDisabled={loading || submitting}
                        ></SizesSelect>
                        <FieldErrors
                          errors={getFieldErrors("sizes", errors) as string[]}
                        ></FieldErrors>
                      </>
                    ) : (
                      <p>{sizes.map((size) => size.size).join(", ")}</p>
                    )}
                  </CCol>
                </CRow>
              </CFormGroup>
            </CTabPane>
            <CTabPane data-tab="custom">
              {items.map((entry, ix) => {
                return (
                  <CRow key={entry.uuid}>
                    <CCol>
                      <ProductCustomPricingFormItem
                        value={entry.item}
                        onDelete={() => onEntryDelete(entry)}
                        onChange={(item) =>
                          onEntryChange({
                            uuid: entry.uuid,
                            item: item,
                          })
                        }
                        errors={ix < itemErrors.length ? itemErrors[ix] : {}}
                      />
                    </CCol>
                  </CRow>
                );
              })}
              {items.length === 0 ? (
                <CRow>
                  <CCol>
                    Agregue nuevas entradas al asiento con el boton + de la
                    derecha
                  </CCol>
                </CRow>
              ) : (
                <></>
              )}
              <CRow className="mb-1">
                <CCol>
                  <CButton
                    className="btn btn-primary float-right"
                    onClick={onAddEntryClick}
                  >
                    <i className="fa fa-plus" />
                  </CButton>
                </CCol>
              </CRow>
            </CTabPane>
          </CTabContent>
        </CTabs>

        <CFormGroup className="float-right">
          <CButtonGroup>
            <CButton type="button" color="secondary" onClick={onClose}>
              Atras
            </CButton>
            <CButton type="submit" color="primary" onClick={onSave}>
              {submitting || loading ? (
                <Spinner
                  animation="grow"
                  style={{
                    height: "17px",
                    width: "17px",
                    marginTop: "auto",
                    marginBottom: "auto",
                    marginRight: "10px",
                  }}
                />
              ) : (
                <></>
              )}
              {buttonText}
            </CButton>
          </CButtonGroup>
        </CFormGroup>
      </fieldset>
    </>
  );
};

export default SupplierForm;
